import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Target, Users, Building2, TrendingUp, Clock, CheckCircle, IndianRupee,
  Sparkles, AlertTriangle, Eye, Coins, UserPlus, X, FileText, Phone, Mail, MapPin, Loader2
} from 'lucide-react';

const STAGE_LABELS = { new: 'New', quotation_sent: 'Quotation Sent', followup: 'Follow-up', negotiation: 'Negotiation', confirmed: 'Confirmed', lost: 'Lost' };
const STAGE_COLORS = { new: 'bg-blue-500', quotation_sent: 'bg-violet-500', followup: 'bg-amber-500', negotiation: 'bg-orange-500', confirmed: 'bg-emerald-500', lost: 'bg-red-500' };

const formatINR = (paise) => '₹' + new Intl.NumberFormat('en-IN').format(paise / 100);

/* ── Company View Dialog (read-only) ───────────────────────── */
function CompanyViewDialog({ companyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api.get(`/admin/company/${companyId}/view`).then(r => setData(r.data)).catch(() => toast.error('Failed to load company data')).finally(() => setLoading(false));
  }, [companyId]);

  if (!companyId) return null;
  const c = data?.company || {};
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'users', label: `Users (${data?.users?.length || 0})` },
    { key: 'leads', label: `Leads (${data?.leads?.length || 0})` },
    { key: 'itineraries', label: `Itineraries (${data?.itineraries?.length || 0})` },
    { key: 'credits', label: 'Credits' },
  ];

  return (
    <Dialog open={!!companyId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0" data-testid="company-view-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            {loading ? 'Loading...' : c.name || 'Company'}
            <Badge variant="outline" className="text-[10px] capitalize ml-2">{c.subscription_plan || 'trial'}</Badge>
            <Badge className={`text-[10px] ml-1 ${c.subscription_status === 'active' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>{c.subscription_status}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex gap-1 px-6 pt-3">
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <ScrollArea className="px-6 pb-5 pt-3" style={{ maxHeight: 'calc(85vh - 140px)' }}>
              {tab === 'overview' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Plan', value: c.subscription_plan || 'trial' },
                    { label: 'Max Users', value: c.max_users || 1 },
                    { label: 'Monthly Credits', value: c.monthly_credits || 500 },
                    { label: 'Credits Used', value: c.credits_used_this_cycle || 0 },
                    { label: 'Created', value: c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '-' },
                    { label: 'Trial Ends', value: c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString('en-IN') : '-' },
                  ].map(f => (
                    <div key={f.label} className="p-3 rounded-lg bg-muted/50 border border-border/30">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{f.label}</p>
                      <p className="text-sm font-semibold mt-0.5 capitalize">{f.value}</p>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'users' && (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">Name</TableHead><TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Role</TableHead><TableHead className="text-xs">Active</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(data?.users || []).map(u => (
                      <TableRow key={u.id}>
                        <TableCell className="text-sm font-medium">{u.name}</TableCell>
                        <TableCell className="text-xs">{u.email}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] capitalize">{u.role?.replace('_',' ')}</Badge></TableCell>
                        <TableCell><Badge className={`text-[10px] ${u.is_active ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>{u.is_active ? 'Yes' : 'No'}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {tab === 'leads' && (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">ID</TableHead><TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs">Destination</TableHead><TableHead className="text-xs">Stage</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(data?.leads || []).slice(0, 50).map(l => (
                      <TableRow key={l.id}>
                        <TableCell className="text-[10px] font-mono">{l.inquiry_id}</TableCell>
                        <TableCell className="text-sm font-medium">{l.client_name}</TableCell>
                        <TableCell className="text-xs">{l.destination || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] capitalize">{STAGE_LABELS[l.stage] || l.stage}</Badge></TableCell>
                        <TableCell className="text-[10px] text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {tab === 'itineraries' && (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">Title</TableHead><TableHead className="text-xs">Destination</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(data?.itineraries || []).slice(0, 50).map(it => (
                      <TableRow key={it.id}>
                        <TableCell className="text-sm font-medium">{it.title || 'Untitled'}</TableCell>
                        <TableCell className="text-xs">{it.destination || '-'}</TableCell>
                        <TableCell className="text-[10px] text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {tab === 'credits' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50 border border-border/30">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Monthly Credits</p>
                      <p className="text-lg font-bold">{data?.credits?.monthly_credits || 0}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50 border border-border/30">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Used This Cycle</p>
                      <p className="text-lg font-bold">{data?.credits?.credits_used || 0}</p>
                    </div>
                  </div>
                  {data?.credits?.recent_transactions?.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Recent Transactions</p>
                      {data.credits.recent_transactions.slice(0, 15).map(t => (
                        <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-border/20 text-xs">
                          <span className="text-muted-foreground">{t.description}</span>
                          <span className={t.amount > 0 ? 'text-emerald-600 font-semibold' : 'text-red-500 font-semibold'}>{t.amount > 0 ? '+' : ''}{t.amount}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Super Admin Dashboard ─────────────────────────────────── */
function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCompanyId, setViewCompanyId] = useState(null);

  useEffect(() => {
    api.get('/admin/platform-overview').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />)}
      </div>
    </div>
  );

  if (!data) return <p className="text-muted-foreground">Failed to load platform data.</p>;

  const rev = data.revenue || {};
  const planDist = data.plan_distribution || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="super-admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Platform Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage companies, monitor onboarding, and track revenue</p>
      </div>

      {/* Stat Cards Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        {[
          { label: 'Total Companies', value: data.total_companies, icon: Building2, color: 'text-violet-500', bg: 'bg-violet-500/10' },
          { label: 'Active', value: data.active_companies, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
          { label: 'Inactive', value: data.inactive_companies, icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10' },
          { label: 'New This Week', value: data.new_signups_week, icon: UserPlus, color: 'text-blue-500', bg: 'bg-blue-500/10' },
        ].map(s => (
          <Card key={s.label} className="border border-border bg-card card-hover-lift">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.15em]">{s.label}</p>
                  <p className="text-2xl font-heading font-bold mt-1.5 text-foreground">{s.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue + Plan Distribution Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Revenue */}
        <Card className="border border-border bg-card" data-testid="revenue-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-emerald-500" /> Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">This Month</p>
                <p className="text-2xl font-heading font-bold text-foreground">{formatINR(rev.this_month_total || 0)}</p>
                <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                  <span>Subscriptions: {formatINR(rev.this_month_subscription || 0)}</span>
                  <span>Credits: {formatINR(rev.this_month_credits || 0)}</span>
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">All Time</p>
                <p className="text-lg font-heading font-bold text-foreground">{formatINR(rev.total_all_time || 0)}</p>
                <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                  <span>{rev.subscription_count || 0} subscriptions</span>
                  <span>{rev.credit_purchase_count || 0} credit purchases</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card className="border border-border bg-card" data-testid="plan-distribution-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" /> Plan Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(planDist).map(([plan, count]) => {
                const total = data.total_companies || 1;
                const pct = Math.round((count / total) * 100);
                const color = plan === 'trial' ? 'bg-amber-500' : plan === 'starter' ? 'bg-blue-500' : plan === 'pro' ? 'bg-indigo-500' : 'bg-slate-400';
                return (
                  <div key={plan}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{plan || 'Trial'}</span>
                      <span className="text-sm font-semibold">{count} <span className="text-xs text-muted-foreground font-normal">({pct}%)</span></span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{data.total_users} total users across all companies</p>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Trials (Needs Attention) */}
      {data.expiring_trials?.length > 0 && (
        <Card className="border-2 border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/10" data-testid="expiring-trials-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" /> Trials Expiring Soon ({data.expiring_trials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.expiring_trials.map(t => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-slate-900 border border-border">
                  <div>
                    <p className="font-medium text-sm">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Expires: {new Date(t.trial_ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${t.days_remaining <= 1 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>
                      {t.days_remaining}d left
                    </Badge>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setViewCompanyId(t.id)}>
                      <Eye className="w-3 h-3" /> View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Onboarding Table */}
      {data.company_onboarding?.length > 0 && (
        <Card className="border border-border bg-card" data-testid="onboarding-table">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" /> All Companies — Onboarding & Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Company</TableHead>
                    <TableHead className="text-xs">Plan</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-center">Users</TableHead>
                    <TableHead className="text-xs text-center">Leads</TableHead>
                    <TableHead className="text-xs text-center">Itineraries</TableHead>
                    <TableHead className="text-xs">Credits Used</TableHead>
                    <TableHead className="text-xs">Joined</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.company_onboarding.map(c => {
                    const creditPct = c.monthly_credits > 0 ? Math.round((c.credits_used / c.monthly_credits) * 100) : 0;
                    const isUsing = c.leads > 0 || c.itineraries > 0;
                    return (
                      <TableRow key={c.id} className={!c.is_active ? 'opacity-50' : ''}>
                        <TableCell className="font-medium text-sm">{c.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] capitalize ${
                            c.plan === 'pro' ? 'border-indigo-300 text-indigo-600' :
                            c.plan === 'starter' ? 'border-blue-300 text-blue-600' :
                            'border-amber-300 text-amber-600'
                          }`}>{c.plan || 'trial'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${c.status === 'active' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                            {c.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-xs">{c.users}</TableCell>
                        <TableCell className="text-center text-xs">{c.leads}</TableCell>
                        <TableCell className="text-center text-xs">{c.itineraries}</TableCell>
                        <TableCell>
                          <span className="text-xs font-mono">{c.credits_used}/{c.monthly_credits}</span>
                          {c.monthly_credits > 0 && (
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden mt-0.5">
                              <div className={`h-full rounded-full ${creditPct > 80 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, creditPct)}%` }} />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-[10px] text-muted-foreground">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary"
                            onClick={() => setViewCompanyId(c.id)} data-testid={`view-company-${c.id}`}>
                            <Eye className="w-3 h-3" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Signups */}
      {data.recent_signups?.length > 0 && (
        <Card className="border border-border bg-card" data-testid="recent-signups-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-blue-500" /> Recent Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_signups.map(c => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] capitalize">{c.subscription_plan || 'trial'}</Badge>
                    <Badge className={`text-[10px] ${c.subscription_status === 'active' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                      {c.subscription_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── CRM User Dashboard (company_admin / manager / agent) ── */
function CRMDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />)}
      </div>
    </div>
  );

  const statCards = [
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: Target, color: 'text-voyage-blue', bg: 'bg-voyage-blue/10', accent: 'stat-card-blue' },
    { label: 'New Leads', value: stats?.new_leads || 0, icon: Clock, color: 'text-blue-500', bg: 'bg-blue-500/10', accent: 'stat-card-blue' },
    { label: 'Confirmed', value: stats?.confirmed || 0, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', accent: 'stat-card-emerald' },
    { label: 'Revenue', value: `₹${(stats?.total_revenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-voyage-orange', bg: 'bg-voyage-orange/10', accent: 'stat-card-orange' }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-home">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {user?.role === 'company_admin' ? 'Company overview' : user?.role === 'manager' ? 'Team performance' : 'Your assignments'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        {statCards.map(stat => (
          <Card key={stat.label} className={`border border-border bg-card card-hover-lift ${stat.accent}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.15em]">{stat.label}</p>
                  <p className="text-2xl font-heading font-bold mt-1.5 text-foreground">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stats?.pipeline && (
        <Card className="border border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" /> Pipeline Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pipeline-breakdown">
              {Object.entries(stats.pipeline).map(([stage, count]) => (
                <div key={stage} className="flex flex-col items-center p-3 rounded-xl bg-muted/50 border border-border/50 card-hover-lift">
                  <div className={`w-3 h-3 rounded-full ${STAGE_COLORS[stage]} mb-2 shadow-sm`} />
                  <span className="text-xl font-heading font-bold text-foreground">{count}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">{STAGE_LABELS[stage]}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-border bg-card card-hover-lift stat-card-emerald"><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-500" /></div>
            <div><p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.15em]">Conversion Rate</p><p className="text-xl font-heading font-bold text-foreground">{stats?.conversion_rate || 0}%</p></div>
          </div>
        </CardContent></Card>
        <Card className="border border-border bg-card card-hover-lift stat-card-amber"><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-500" /></div>
            <div><p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.15em]">Pending Follow-ups</p><p className="text-xl font-heading font-bold text-foreground">{stats?.pending_followups || 0}</p></div>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

/* ── Main Export ─────────────────────────────────────────── */
export default function DashboardHome() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') return <SuperAdminDashboard />;
  return <CRMDashboard />;
}
